import React, { useEffect, useState } from "react";
import { makeStyles } from "@material-ui/core/styles";
import Input from "sushiweb/InputTypes/Input";
import Button from "sushiweb/Button";
import Textarea from "sushiweb/InputTypes/Textarea";
import FormControl from "@material-ui/core/FormControl";
import { connect } from "react-redux";
import { bindActionCreators } from "redux";
import Checkbox from "sushiweb/InputTypes/Checkbox";
import TextField from "@material-ui/core/TextField";

import {
  isValidated,
  getInitialState,
  filterObj,
  getPath,
} from "../../../utils";
import * as LocalityActions from "../../../store/actions/Locality";
import * as CityActions from "../../../store/actions/City";
import * as UserActions from "../../../store/actions/User";
import InfiniteScrollDropdown from "../../Common/InfiniteScrollDropdown";
import Permission from "../../Common/Permission";
import MenuNames from "../../../constants/Menu/MenuNames.json";
import MenuActionsMappings from "../../../constants/Menu/MenuActionsMappings";
import {
  BottomSectionModal,
  FlexContainer,
} from "../../../constants/assets/containers";
import NoFooterModal from "../../Common/NoFooterModal";
import variables from "../../../constants/assets/variables";

const useStyles = makeStyles((theme) => ({
  root: {
    "& > *": {
      margin: theme.spacing(1),
    },
  },
  fieldsMargin: {
    margin: "10px 0",
  },
  errorSpacing: {
    marginTop: "25px",
  },
}));

const mandatoryFields = ["locality.locality_name", "locality.city_id"];
const editableFields = [
  "locality.locality_name",
  "locality.city_id",
  "locality.locality_supported",
  "locality.created_at",
  "locality.update_at",
  "locality.latitude",
  "locality.longitude",
  "locality.geo_bound",
  "locality.is_pickup_serviceable",
  "locality.pickup_users_available",
  "locality_service_hours.begin_time",
  "locality_service_hours.end_time",
  "locality.is_merchant_ob_blacklisted",
  "boundary",
];

const LocalityFormModal = (props) => {
  const classes = useStyles();
  const allActions = MenuActionsMappings[MenuNames.LOCALITY];
  const [initialState, setInitialState] = useState({
    "locality.locality_name": "",
    "locality.city_id": "",
    "locality.locality_supported": false,
    "locality.created_at": "",
    "locality.update_at": "",
    "locality.latitude": "",
    "locality.longitude": "",
    "locality.geo_bound": "",
    "locality.is_pickup_serviceable": false,
    "locality.pickup_users_available": false,
    "locality.is_test": false,
    "locality.is_merchant_ob_blacklisted": false,
    "locality_service_hours.begin_time": "",
    "locality_service_hours.end_time": "",
    boundary: "",
  });
  const {
    modalState,
    handleClose,
    activeId,
    activeLocality,
    activeFilters,
    filterFields,
    setStatus,
    currentPage,
  } = props;

  const handleSubmit = (evt) => {
    evt.preventDefault();
    // if filter button is clicked, then apply filters
    if (modalState === "Filter") {
      const filters = {};
      for (let key of filterFields) {
        if (initialState[key]) {
          filters[key] = initialState[key];
        }
      }
      props.getLocalityList(filters, {}).then((res) => {
        handleClose();
      });
    } else {
      const body = filterObj(initialState, editableFields);
      let validatedState = isValidated(body, mandatoryFields);

      if (validatedState === "validated") {
        // boundary should be in correct array format.
        // we test for correct JSON here which will produce the
        // desired test.
        if (getPath(body, "boundary.length", 0) > 0) {
          try {
            JSON.parse(body.boundary);
          } catch (err) {
            validatedState =
              "Boundary is not in correct format. Sample value: [[12.957212, 77.616819],[13.958689, 14.6209],[12.957212, 77.616819]]";
          }
        } else {
          body.boundary = "";
        }
      } else {
        setStatus({ type: "error", message: validatedState });
      }

      // send different requests for Edit and Add state.
      if (validatedState === "validated") {
        if (modalState === "Edit") {
          props.updateLocality(activeId, body).then((res) => {
            const pageRequest = { page: currentPage };
            props.getLocalityList(activeFilters, pageRequest);
            handleClose();
          });
        } else if (modalState === "Add") {
          props.createLocality(body).then((res) => {
            const pageRequest = { page: currentPage };
            props.getLocalityList(activeFilters, pageRequest);
            handleClose();
          });
        }
      } else {
        setStatus({ type: "error", message: validatedState });
      }
    }
  };

  // clears all applied filters.
  const handleClearFilters = () => {
    props.getLocalityList({}).then((res) => {
      props.resetState({ state: "activeFilters" });
      handleClose();
    });
  };

  const handleChange = (key, val) => {
    const values = { ...initialState };
    values[key] = val;
    setInitialState(values);
  };

  useEffect(() => {
    if (activeId) {
      props.getLocality(activeId);
    }

    // reset the state to show empty modal
    if (modalState === "Add" && Object.keys(activeLocality).length > 0) {
      props.resetState({ state: "activeLocality" });
    }
  }, [activeId, modalState]);

  useEffect(() => {
    // update the initialState on modal render.
    // this is not needed when modalState is "Add".
    if (modalState === "Edit" && Object.keys(activeLocality).length > 0) {
      const values = { ...initialState };
      Object.keys(values).map((key) => {
        values[key] = activeLocality[key]
          ? activeLocality[key]
          : getInitialState(initialState, key);
      });

      setInitialState(values);
    } else if (
      Object.keys(activeFilters).length > 0 &&
      modalState === "Filter"
    ) {
      const values = { ...initialState };
      Object.keys(values).map((key) => {
        values[key] = activeFilters[key]
          ? activeFilters[key]
          : getInitialState(initialState, key);
      });
      setInitialState(values);
    }
  }, [activeLocality, activeFilters]);

  const isDisabledField = (value) => {
    if (modalState === "Edit") {
      if (editableFields.includes(value)) {
        return false;
      }
      return true;
    } else if (modalState === "Filter") {
      if (filterFields.includes(value)) {
        return false;
      }
      return true;
    } else {
      return false;
    }
  };

  const getRequiredButtonPerms = () => {
    if (modalState === "Edit") {
      return [allActions.UPDATE];
    } else if (modalState === "Add") {
      return [allActions.CREATE];
    }
    return undefined;
  };

  const isHiddenField = (key) => {
    if (modalState === "Filter") {
      return !filterFields.includes(key);
    }
    return false;
  };

  const formElements = [
    {
      key: "locality.locality_name",
      element: (
        <Input
          value={initialState["locality.locality_name"]}
          disabled={isDisabledField("locality.locality_name")}
          data-testid="localityName"
          id="sushi-input"
          onChange={(e) => handleChange("locality.locality_name", e)}
          label="Name"
        />
      ),
    },
    {
      key: "locality.city_id",
      element: (
        <InfiniteScrollDropdown
          priorityFlag={1}
          id="localityCityDropdown"
          getActiveList={props.getCityList}
          nextPage={props.nextPageCity}
          resKey="cities"
          modalState={modalState}
          keyName="locality.city_id"
          labelKey="city_name"
          getDataLabelKey="city.city_name"
          keyTypeToSet="id"
          disabled={isDisabledField("locality.city_id")}
          label="City"
          handleChange={handleChange}
          remoteSearchApplicable
          activeFilters={activeFilters}
          activeElement={activeLocality}
          getActiveItemById={props.getCity}
          appliedFilters={{}}
          remoteSearchKey="startswith"
        />
      ),
    },
    {
      key: "locality.latitude",
      element: (
        <Input
          value={initialState["locality.latitude"]}
          disabled={isDisabledField("locality.latitude")}
          data-testid="localityLatitude"
          id="sushi-input"
          onChange={(e) => handleChange("locality.latitude", e)}
          label="Latitude"
        />
      ),
    },
    {
      key: "locality.longitude",
      element: (
        <Input
          value={initialState["locality.longitude"]}
          disabled={isDisabledField("locality.longitude")}
          data-testid="localityLongitude"
          id="sushi-input"
          onChange={(e) => handleChange("locality.longitude", e)}
          label="Longitude"
        />
      ),
    },
    {
      key: "checkbox-form-group",
      element: (
        <FlexContainer
          style={{ flexWrap: "wrap", justifyContent: "flex-start" }}
          className="mb-20"
        >
          <Checkbox
            disabled={isDisabledField("locality.locality_supported")}
            value="localitySupported"
            color={variables.red500}
            checked={initialState["locality.locality_supported"]}
            onChange={(e) =>
              handleChange("locality.locality_supported", e.localitySupported)
            }
            className="sushi-checkbox"
            data-testid="localitySupported"
            label={"Is Locality Supported"}
          />
          <Checkbox
            disabled={isDisabledField("locality.is_pickup_serviceable")}
            checked={initialState["locality.is_pickup_serviceable"]}
            color={variables.red500}
            value="pickupServicable"
            onChange={(e) =>
              handleChange("locality.is_pickup_serviceable", e.pickupServicable)
            }
            className="sushi-checkbox"
            name="localityPickupServiceable"
            label="Is Pickup Serviceable"
            data-testid="localityPickupServiceable"
          />
          <Checkbox
            disabled={isDisabledField("locality.pickup_users_available")}
            checked={initialState["locality.pickup_users_available"]}
            color={variables.red500}
            value="pickupUsersAvailable"
            onChange={(e) =>
              handleChange(
                "locality.pickup_users_available",
                e.pickupUsersAvailable
              )
            }
            name="localityPickupUsersAvailable"
            className="sushi-checkbox"
            label="Pickup Users Available"
            data-testid="localityPickupUsersAvailable"
          />
          <Checkbox
            disabled
            checked={initialState["locality.is_test"]}
            color={variables.red500}
            value="isTest"
            onChange={(e) => handleChange("locality.is_test", e.isTest)}
            name="localityIsTest"
            className="sushi-checkbox"
            data-testid="localityIsTest"
            label="Is Test?"
          />
          <Checkbox
            disabled={isDisabledField("locality.is_merchant_ob_blacklisted")}
            value="merchantOBBlacklisted"
            color={variables.red500}
            checked={initialState["locality.is_merchant_ob_blacklisted"]}
            onChange={(e) =>
              handleChange("locality.is_merchant_ob_blacklisted", e.merchantOBBlacklisted)
            }
            className="sushi-checkbox"
            data-testid="localityIsMerchantOBBlacklisted"
            label={"Is Merchant OB Blacklisted"}
          />
        </FlexContainer>
      ),
    },
    {
      key: "boundary",
      element: (
        <Textarea
          id="sushi-textarea"
          data-testid="localityBoundary"
          borderColor={variables.primaryColor}
          innerWidth="100%"
          className="mb-20"
          rows={4}
          resize="none"
          label="Boundary"
          disabled={isDisabledField("boundary")}
          onChange={(e) => handleChange("boundary", e)}
          value={initialState.boundary}
        />
      ),
    },
    {
      key: "locality-service-hours-heading",
      element: <p className="medium mt-20">Locality Serviceable Timings</p>,
    },
    {
      key: "locality_service_hours.begin_time",
      element: (
        <FormControl
          required
          fullWidth
          className={classes.fieldsMargin}
          variant="standard"
        >
          <TextField
            disabled={isDisabledField("locality_service_hours.begin_time")}
            fullWidth
            id="time"
            label="Begin"
            type="time"
            value={initialState["locality_service_hours.begin_time"]}
            onChange={(e) =>
              handleChange("locality_service_hours.begin_time", e.target.value)
            }
            InputLabelProps={{
              shrink: true,
            }}
            inputProps={{
              step: 1,
              "data-testid": "localityServiceHoursBeginTime",
            }}
          />
        </FormControl>
        // <Input
        //   value={initialState["locality_service_hours.begin_time"] || " "}
        //   step="1"
        //   type="time"
        //   disabled={isDisabledField("locality_service_hours.begin_time")}
        //   data-testid="localityServiceHoursBeginTime"
        //   id="sushi-input"
        //   onChange={(e) => handleChange("locality_service_hours.begin_time", e)}
        //   label="Begin"
        // />
      ),
    },
    {
      key: "locality_service_hours.end_time",
      element: (
        <FormControl
          required
          fullWidth
          className={classes.fieldsMargin}
          variant="standard"
        >
          <TextField
            disabled={isDisabledField("locality_service_hours.end_time")}
            fullWidth
            id="time"
            label="End"
            type="time"
            value={initialState["locality_service_hours.end_time"]}
            onChange={(e) =>
              handleChange("locality_service_hours.end_time", e.target.value)
            }
            InputLabelProps={{
              shrink: true,
            }}
            inputProps={{
              step: 1,
              "data-testid": "localityServiceHoursEndTime",
            }}
          />
        </FormControl>
      ),
    },
  ];

  return (
    <NoFooterModal
      header={true}
      heading={`${modalState} Locality`}
      handleClose={handleClose}
      id="LocalityFormModal"
      modalButtons={
        <BottomSectionModal className="flex items-center justify-between">
          {/* show the clear button when clicked on filters button */}
          {modalState === "Filter" && (
            <Button
              data-testid="filterFeatureBtn"
              onClick={handleClearFilters}
              className="sushi-btn"
            >
              Clear
            </Button>
          )}
          <Permission requiredPerms={getRequiredButtonPerms()}>
            <Button
              data-testid="submitButton"
              className="sushi-btn"
              onClick={handleSubmit}
            >
              {modalState === "Filter" ? "Apply" : "Submit"}
            </Button>
          </Permission>
        </BottomSectionModal>
      }
    >
      {formElements
        .filter((obj) => !isHiddenField(obj.key))
        .map((obj) => obj.element)}
    </NoFooterModal>
  );
};

const mapStateToProps = (state) => {
  const { Locality, City } = state;
  return {
    activeLocality: Locality.activeLocality,
    activeFilters: Locality.activeFilters,
    currentPage: Locality.currentPage,
    nextPageCity: City.nextPage,
  };
};

const mapDispatchToProps = (dispatch) => {
  return {
    createLocality: bindActionCreators(
      LocalityActions.createLocality,
      dispatch
    ),
    getLocalityList: bindActionCreators(
      LocalityActions.getLocalityList,
      dispatch
    ),
    getLocality: bindActionCreators(LocalityActions.getLocality, dispatch),
    resetState: bindActionCreators(LocalityActions.resetState, dispatch),
    updateLocality: bindActionCreators(
      LocalityActions.updateLocality,
      dispatch
    ),
    getCityList: bindActionCreators(CityActions.getCityList, dispatch),
    getCity: bindActionCreators(CityActions.getCity, dispatch),
    setStatus: bindActionCreators(UserActions.setStatus, dispatch),
  };
};

export default connect(mapStateToProps, mapDispatchToProps)(LocalityFormModal);
